import { useEffect, useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { ChoiceControl, ContentSkeleton, SearchField, ListRow, StatusBadge } from '../../design-system';
import { useAsyncResource } from '../../shared/useAsyncResource';
import type { FeatureReleaseUser } from './model';
import type { FeatureReleasePort } from './port';

export function FeatureReleaseUserPicker({ port, selectedPersonIds, onSelect, onUsersLoaded, disabled = false, error, preview = false }: {
  port: FeatureReleasePort;
  selectedPersonIds: readonly string[];
  onSelect: (user: FeatureReleaseUser) => void;
  onUsersLoaded?: (users: FeatureReleaseUser[]) => void;
  disabled?: boolean;
  error?: string;
  preview?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(query.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [query]);
  const resource = useAsyncResource(() => port.searchUsers(search), [port, search], '사용자 목록을 불러오지 못했습니다.');
  useEffect(() => { if (resource.data) onUsersLoaded?.(resource.data); }, [onUsersLoaded, resource.data]);
  return <Stack spacing={1}>
    <SearchField
      hideLabel={false}
      label={preview ? '확인할 사용자 검색' : '공개할 사용자 검색'}
      placeholder="이름·사번·조직 검색"
      value={query}
      onValueChange={setQuery}
      disabled={disabled}
      error={Boolean(error)}
      helperText={error}
    />
    {resource.loading ? <ContentSkeleton variant="list" rows={3} label="사용자 검색 중" /> : resource.error ? <Alert severity="error" action={<Button onClick={resource.reload}>다시 시도</Button>}>{resource.error.message}</Alert> : <Box role="group" aria-label="사용자 검색 결과" sx={{ maxHeight: 240, overflowY: 'auto' }}>
      {resource.data?.length ? resource.data.map((user) => <ListRow
        key={user.personId}
        label={user.displayName}
        description={[user.workerNumber, user.organization].filter(Boolean).join(' · ')}
        density="compact"
        inset="none"
        onClick={preview ? () => onSelect(user) : undefined}
        ariaLabel={preview ? `${user.displayName} ${user.workerNumber} 확인` : undefined}
        disabled={disabled}
        trailing={preview ? <StatusBadge label={selectedPersonIds.includes(user.personId) ? '선택됨' : user.eligible ? '사용 중' : '사용 불가'} tone={selectedPersonIds.includes(user.personId) ? 'info' : 'neutral'} /> : <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          {!user.eligible && <StatusBadge label="사용 불가" tone="neutral" />}
          <ChoiceControl
          kind="checkbox"
          ariaLabel={`${user.displayName} ${user.workerNumber} 공개 대상`}
          checked={selectedPersonIds.includes(user.personId)}
          disabled={disabled || (!user.eligible && !selectedPersonIds.includes(user.personId))}
          onCheckedChange={() => onSelect(user)}
        /></Stack>}
      />) : <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>검색 조건에 맞는 사용자가 없습니다.</Typography>}
    </Box>}
  </Stack>;
}
